<template>
    <!--
        The full-screen player, opened from the bar: big cover, scrubber and
        controls, like a music app's "now playing". Swipe down, the chevron or
        Escape closes it. Not teleported: dark mode is a class on App.vue's root.
    -->
    <transition name="ab-sheet">
        <div
            v-if="player.sheetOpen && t"
            class="ab-theme ab-np print:hidden"
            :class="player.book?.theme"
            role="dialog"
            aria-modal="true"
            :aria-label="`Now playing: ${t.label}, ${t.title}`"
            :style="dragY ? { transform: `translateY(${dragY}px)`, transition: 'none' } : null"
            @touchstart.passive="touchStart"
            @touchmove.passive="touchMove"
            @touchend="touchEnd"
        >
            <div class="ab-np-inner">
                <header class="flex items-center justify-between gap-2 py-2">
                    <button ref="close" type="button" class="ab-ctl" aria-label="Close player" @click="close"><player-icon name="down" class="h-6 w-6" /></button>
                    <div class="min-w-0 text-center">
                        <p class="ab-eyebrow">Now playing</p>
                        <p class="ab-np-book">{{ player.book.title }}</p>
                    </div>
                    <button type="button" class="ab-ctl" aria-label="All chapters" @click="toChapters"><player-icon name="list" class="h-5 w-5" /></button>
                </header>

                <div class="ab-np-art">
                    <img :src="player.book.cover" :alt="player.book.title" />
                </div>

                <div class="text-center">
                    <p class="ab-eyebrow !text-xs">{{ t.label }}</p>
                    <h2 class="ab-np-title">{{ t.title }}</h2>
                    <p v-if="t.subtitle" class="ab-np-sub">{{ t.subtitle }}</p>
                </div>

                <seek-bar big class="mt-4" />

                <div class="mt-3 flex items-center justify-between">
                    <button type="button" class="ab-ctl ab-ctl-lg" aria-label="Previous chapter" :disabled="!canPrev" @click="previous"><player-icon name="prev" class="h-7 w-7" /></button>
                    <button type="button" class="ab-ctl ab-ctl-lg" aria-label="Back 15 seconds" @click="skip(-15)"><player-icon name="back15" class="h-8 w-8" /></button>
                    <button type="button" class="ab-ctl-play ab-np-play" :aria-label="player.playing ? 'Pause' : 'Play'" @click="toggle">
                        <player-icon :name="player.playing ? 'pause' : 'play'" class="h-8 w-8" :class="{ 'ml-1': !player.playing }" />
                    </button>
                    <button type="button" class="ab-ctl ab-ctl-lg" aria-label="Forward 30 seconds" @click="skip(30)"><player-icon name="fwd30" class="h-8 w-8" /></button>
                    <button type="button" class="ab-ctl ab-ctl-lg" aria-label="Next chapter" :disabled="!canNext" @click="next"><player-icon name="next" class="h-7 w-7" /></button>
                </div>

                <div class="mt-4 flex items-center justify-center gap-3">
                    <button type="button" class="ab-speed" :aria-label="`Playback speed ${player.rate}×. Change`" @click="cycleRate">{{ player.rate }}×</button>
                    <sleep-timer />
                </div>

                <button v-if="upNext" type="button" class="ab-np-next" @click="play(upNext)">
                    <span class="ab-eyebrow">Up next</span>
                    <span class="ab-np-next-title">{{ upNext.label }} · {{ upNext.title }}</span>
                </button>
            </div>
        </div>
    </transition>
</template>

<script>
import { player, play, toggle, skip, previous, next, neighbour, setRate, RATES } from '../../helpers/audiobook'
import PlayerIcon from './playerIcon.vue'
import SeekBar from './seekBar.vue'
import SleepTimer from './sleepTimer.vue'

export default {
    name: 'NowPlaying',
    components: { PlayerIcon, SeekBar, SleepTimer },
    data() {
        return { player, dragY: 0, startY: null }
    },
    computed: {
        t() { return player.current },
        upNext() { return neighbour(1) },
        canPrev() { return player.position > 5 || !!neighbour(-1) },
        canNext() { return !!neighbour(1) },
    },
    watch: {
        'player.sheetOpen'(open) {
            // the page behind stays put while the sheet is up
            document.documentElement.style.overflow = open ? 'hidden' : ''
            if (open) this.$nextTick(() => this.$refs.close?.focus())
        },
    },
    mounted() { document.addEventListener('keydown', this.onKey) },
    unmounted() {
        document.removeEventListener('keydown', this.onKey)
        document.documentElement.style.overflow = ''
    },
    methods: {
        play, toggle, skip, previous, next,
        close() { player.sheetOpen = false },
        cycleRate() { setRate(RATES[(RATES.indexOf(player.rate) + 1) % RATES.length]) },
        toChapters() {
            this.close()
            this.$router.push({ path: `/audiobooks/${player.slug}`, hash: `#track-${this.t.number}` })
        },
        onKey(e) {
            if (player.sheetOpen && e.key === 'Escape') this.close()
        },
        // Swipe down to dismiss, as on a phone's music player.
        touchStart(e) { this.startY = e.touches[0].clientY },
        touchMove(e) {
            if (this.startY == null || e.target.closest('input')) return
            this.dragY = Math.max(0, e.touches[0].clientY - this.startY)
        },
        touchEnd() {
            if (this.dragY > 110) this.close()
            this.dragY = 0
            this.startY = null
        },
    },
}
</script>

<style>
.ab-np {
    position: fixed; inset: 0; z-index: 10000; overflow-y: auto; color: var(--ab-ink);
    background: linear-gradient(to bottom, var(--ab-bg), var(--ab-cream));
    padding: calc(env(safe-area-inset-top) + 0.5rem) env(safe-area-inset-right) calc(1.25rem + env(safe-area-inset-bottom)) env(safe-area-inset-left);
    transition: transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.ab-np-inner { margin: 0 auto; display: flex; min-height: 100%; max-width: 28rem; flex-direction: column; padding: 0 1.5rem; }
.ab-np-book { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-family: ui-serif, Georgia, serif; font-size: 0.875rem; font-style: italic; color: var(--ab-muted); }
.ab-np-art { display: flex; flex: 1 1 0%; align-items: center; justify-content: center; padding: 1.5rem 0; min-height: 12rem; }
.ab-np-art img { width: auto; max-height: min(42vh, 22rem); border-radius: 3px; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.45); }
.ab-np-title { margin-top: 0.25rem; font-family: ui-serif, Georgia, serif; font-size: 1.75rem; font-weight: 600; line-height: 1.2; color: var(--ab-ink); }
.ab-np-sub { margin-top: 0.25rem; font-family: ui-serif, Georgia, serif; font-size: 1rem; font-style: italic; color: var(--ab-muted); }
.ab-ctl-lg { width: 3.5rem; height: 3.5rem; }
.ab-np-play { width: 5rem; height: 5rem; }
.ab-np-next { margin-top: 1.5rem; display: flex; width: 100%; flex-direction: column; align-items: flex-start; gap: 0.125rem; border-radius: 0.75rem; border: 1px solid var(--ab-line); background: color-mix(in srgb, var(--ab-bg) 70%, transparent); padding: 0.75rem 1rem; text-align: left; transition: border-color 0.15s; }
.ab-np-next:hover { border-color: var(--ab-gold); }
.ab-np-next-title { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-family: ui-serif, Georgia, serif; font-size: 1rem; font-weight: 600; color: var(--ab-ink); }
.ab-sheet-enter-active, .ab-sheet-leave-active { transition: transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.3s; }
.ab-sheet-enter-from, .ab-sheet-leave-to { transform: translateY(100%); opacity: 0.6; }
@media (prefers-reduced-motion: reduce) { .ab-sheet-enter-active, .ab-sheet-leave-active, .ab-np { transition: none; } }
</style>
